import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, catchError, combineLatest, filter, of, switchMap, take, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PortalAuthService } from '../auth/portal-auth.service';
import { NotificationApiService } from './notification-api.service';
import { SignalrMessageAdapter } from '../eventhub/signalr-message-adapter';
import { PermissionService } from './permission.service';
import { FeatureFlagService } from './feature-flag.service';
import { AlertService } from './alert.service';
import { AlertType } from '../components/global-alert/alert-type.enum';
import { ActiveTenantService } from './active-tenant.service';
import { AdminApiService, AdminTenant } from './admin-api.service';
import { MenuConfigService } from './menu-config.service';
import { PortalFeature } from '../domain/member-permission.model';

/**
 * Cross-cutting shell orchestration extracted from AppComponent.ngOnInit:
 * SignalR lifecycle, unread-count management, permission loading, real-time
 * command-failure alerts, reconnect toast, and success toasts for the user's
 * own mutations. Call once from the shell.
 *
 * Reconnect/visibilitychange resync is handled per-store via wireReconnect().
 */
@Injectable({ providedIn: 'root' })
export class AppBootstrapService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private _currentUserSub: string | null = null;

  constructor(
    private readonly portalAuth: PortalAuthService,
    private readonly notificationApi: NotificationApiService,
    private readonly signalr: SignalrMessageAdapter,
    private readonly permissionService: PermissionService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly alertService: AlertService,
    private readonly translate: TranslateService,
    private readonly activeTenant: ActiveTenantService,
    private readonly adminApi: AdminApiService,
    private readonly router: Router,
    private readonly menuConfig: MenuConfigService,
  ) {}

  start(): void {
    this.preselectTenantForAdmin();
    // Cache the Auth0 sub so we can compare issuedBy on incoming events.
    this.portalAuth.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this._currentUserSub = user?.sub ?? null;
    });

    // Connect SignalR and load initial unread count on login.
    this.portalAuth.isAuthenticated$.pipe(
      filter(Boolean),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.signalr.connect();
      this.notificationApi.refreshUnreadCount();
    });

    // Load user permissions once after login.
    this.portalAuth.isAuthenticated$.pipe(
      filter(Boolean),
      switchMap(() => this.permissionService.load()),
      takeUntil(this.destroy$),
    ).subscribe();

    // Live permission-downgrade toast: if someone else lowers a permission the caller is
    // relying on for the page they currently have open, warn before they lose an
    // in-progress edit — the backend already rejects the save (403), this just tells them
    // why up front, with a one-click way to resync. Irrelevant if the downgrade is for a
    // feature other than the one currently on screen.
    this.permissionService.downgrades$.pipe(takeUntil(this.destroy$)).subscribe(downgrade => {
      if (downgrade.feature !== this.currentRouteFeature()) return;
      this.alertService.show(
        AlertType.Warning,
        this.translate.instant('zugriff.permissionChangedToast'),
        { label: this.translate.instant('common.reload'), onClick: () => window.location.reload() },
      );
    });

    // Load the tenant's effective feature flags once after login (docs/KONZEPT-Tenant-Feature-Flags.md).
    this.portalAuth.isAuthenticated$.pipe(
      filter(Boolean),
      switchMap(() => this.featureFlagService.load()),
      takeUntil(this.destroy$),
    ).subscribe();

    // Feature 4: increment badge on each entity change (replaces 60 s poll).
    // Own mutations are excluded: they get the success toast below instead, and the
    // server's unread count leaves them out too — incrementing here would let the local
    // badge drift above the real count until the next refetch.
    this.signalr.entityChanged$.pipe(
      filter(e =>
        e.notificationType !== 'CommandFailed' &&
        // Data-only events (device telemetry, platform-admin copies) store no notification, so
        // the server's unread count never includes them.
        e.notify !== false &&
        !(!!this._currentUserSub && e.issuedBy === this._currentUserSub)
      ),
      takeUntil(this.destroy$),
    ).subscribe(() => this.notificationApi.increment());

    // Full badge refresh on reconnect: catches any events missed during the gap. The user hears about the
    // restored connection from the connection banner (ConnectivityService).
    this.signalr.reconnected$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.notificationApi.refreshUnreadCount();
    });

    // Surface real-time command-failure alerts.
    this.signalr.commandFailed$.pipe(takeUntil(this.destroy$)).subscribe(e =>
      this.alertService.show(AlertType.Error, e.message),
    );

    // Feature 3: success toast for the current user's own mutations.
    this.signalr.entityChanged$.pipe(
      filter(e =>
        e.notificationType !== 'CommandFailed' &&
        !!this._currentUserSub &&
        e.issuedBy === this._currentUserSub
      ),
      takeUntil(this.destroy$),
    ).subscribe(e => {
      const msg = this.buildSuccessMessage(e.notificationType);
      if (msg) this.alertService.show(AlertType.Success, msg);
    });
  }

  /**
   * A platform admin signing in with a personal account belongs to no tenant, so
   * AuthMiddleware resolves an empty TenantId and every tenant-scoped endpoint
   * answers 404 (GET /api/tenants/me and friends) until an "act as" tenant is
   * picked — which is why the shell used to come up empty until the first manual
   * tenant switch. Pre-select their first tenant instead.
   *
   * Skipped once they've explicitly chosen global admin mode, so this never
   * silently undoes that. Reloading afterwards mirrors the manual switcher: the
   * X-Active-Tenant-Id header is attached per request, so pages that already
   * fetched in the tenant-less context have to fetch again to see anything.
   */
  private preselectTenantForAdmin(): void {
    combineLatest([this.portalAuth.isAuthenticated$, this.portalAuth.isPlatformAdmin$]).pipe(
      filter(([isAuthenticated, isPlatformAdmin]) => isAuthenticated === true && isPlatformAdmin === true),
      take(1),
      filter(() => !this.activeTenant.activeId && !this.activeTenant.wasExplicitlyCleared),
      switchMap(() => this.adminApi.getTenants().pipe(
        catchError(err => {
          console.warn('Could not pre-select a tenant for this platform admin; ' +
            'tenant-scoped views stay empty until one is picked manually.', err);
          return of([] as AdminTenant[]);
        }),
      )),
      takeUntil(this.destroy$),
    ).subscribe(tenants => {
      if (!tenants.length) return;
      // Same ordering the tenant switcher shows, so "the first one" means the
      // same thing in both places.
      const [first] = [...tenants].sort((a, b) => a.name.localeCompare(b.name));
      this.activeTenant.set({ id: first.id, name: first.name });
      window.location.reload();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Reverse-looks-up the PortalFeature backing the currently active route, via the same
   *  MenuConfigService.portalPages mapping the nav menu uses — longest-prefix match so a
   *  sub-route (e.g. a gateway detail page under /devices) still resolves to its parent
   *  page's feature. Undefined on pages with no feature gate (home, profile, admin, …). */
  private currentRouteFeature(): PortalFeature | undefined {
    const url = this.router.url.split('?')[0].split('#')[0];
    const match = this.menuConfig.portalPages
      .filter(p => p.feature && (url === p.url || url.startsWith(p.url + '/')))
      .sort((a, b) => b.url.length - a.url.length)[0];
    return match?.feature;
  }

  /** Success types that get a "your own action succeeded" toast. Each maps to a
   *  `toast.<type>` i18n key, resolved at display time so the language is respected. */
  private static readonly SUCCESS_TOAST_TYPES = new Set<string>([
    'DepartmentCreated', 'DepartmentUpdated', 'DepartmentDeleted',
    'MenuCreated', 'MenuUpdated', 'MenuDeleted',
    'RoleCreated', 'RoleUpdated', 'RoleDeleted',
    'JobPositionCreated', 'JobPositionUpdated', 'JobPositionDeleted',
    'MemberInvited', 'MemberUpdated', 'MemberRevoked',
    'InvoiceCreated', 'InvoiceUpdated', 'InvoicePaid',
    'GatewayRegistered', 'GatewayUpdated', 'GatewayDeleted',
    'TenantFeatureUpdated',
  ]);

  private buildSuccessMessage(notificationType: string): string | null {
    return AppBootstrapService.SUCCESS_TOAST_TYPES.has(notificationType)
      ? this.translate.instant(`toast.${notificationType}`)
      : null;
  }
}
